/**
 * Cost classes of the Echo cost ledger (EMC contract C3.7, plan
 * plans/2026-10-21-echo-whatsapp-marketing-costs-ledger-plan.md §4.1).
 *
 * The class is decided in code from the row's `cost_domain`, never in config,
 * and mirrors the indexer's constant:
 * - `whatsapp` → `messaging`: talking to the customer about this Echo;
 * - `marketing` → `acquisition`: getting the customer to make this Echo
 *   (allocated ad and WhatsApp marketing spend, admin only);
 * - every other domain → `production`: making the Echo.
 *
 * Decision D1: `echo_cost_total_usd` stays production only, so every number
 * that called itself "the Echo's cost" before EMC stays comparable with
 * history. Messaging and acquisition are shown beside it, never inside it.
 */

export type EchoCostClass = "production" | "messaging" | "acquisition";

export const ECHO_COST_CLASSES: readonly EchoCostClass[] = [
  "production",
  "messaging",
  "acquisition",
];

/** The two domains that are not production. */
export const WHATSAPP_COST_DOMAIN = "whatsapp";
export const MARKETING_COST_DOMAIN = "marketing";

export function costClassOf(domain?: string | null): EchoCostClass {
  // Legacy rows predate `cost_domain` and are always i2v (production).
  const key = (domain ?? "i2v").trim().toLowerCase();
  if (key === WHATSAPP_COST_DOMAIN) return "messaging";
  if (key === MARKETING_COST_DOMAIN) return "acquisition";
  return "production";
}

export function isProductionDomain(domain?: string | null): boolean {
  return costClassOf(domain) === "production";
}

export const COST_CLASS_LABELS: Record<EchoCostClass, string> = {
  production: "Production",
  messaging: "WhatsApp",
  acquisition: "Marketing",
};

/**
 * Billing channels that only messaging and acquisition rows use. They are
 * left out of production funding splits, which would otherwise double count
 * the WhatsApp line the Costs page adds on its own.
 */
export const NON_PRODUCTION_CHANNELS: ReadonlySet<string> = new Set([
  "meta_whatsapp",
  "meta_ads",
]);
